/**
 * ManualsHome — /#/manuals
 * Index page for AIP Operating Manuals. Lists 5 role cards.
 * W66.X17: Added doctrine banner and Reference Library crosslink.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 */

import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const RED = "#DC2626";

interface RoleCard {
  slug: string;
  label: string;
  name: string;
  summary: string;
  inside: string[];
  status: "available" | "soon";
}

const CARDS: RoleCard[] = [
  {
    slug: "marketing",
    label: "Marketing",
    name: "Marketing operating manual",
    summary: "Top-of-funnel pacing, hooks, MQL volume, and the Annual Commitment Grid.",
    inside: [
      "Building the Annual Commitment Grid",
      "Annual Activity Calendar",
      "Approving outreach hooks",
      "Reading Plan vs Actual",
    ],
    status: "available",
  },
  {
    slug: "bdr",
    label: "BDR",
    name: "BDR operating manual",
    summary: "Daily discovery cadence, MQL acceptance, and qualification checkpoints.",
    inside: [
      "Daily cadence and queue",
      "Accepting and returning MQLs",
      "Discovery against the 10 checkpoints",
      "Hook pull-through into outbound",
    ],
    status: "available",
  },
  {
    slug: "ae",
    label: "AE",
    name: "AE operating manual",
    summary: "Pipeline build, account plans, and forecast hygiene.",
    inside: [
      "Daily account view and Plan vs Actual",
      "Helix opportunity DNA",
      "Forecast and pipeline coverage",
      "Account scorecard rituals",
    ],
    status: "available",
  },
  {
    slug: "manager",
    label: "Manager",
    name: "Manager operating manual",
    summary: "Coaching, reconciliation, and team scoreboard reading.",
    inside: [
      "Friday MQL reconciliation",
      "Cadence / Coaching and override policy",
      "Team scoreboard interpretation",
      "Plan vs Actual for the team",
    ],
    status: "available",
  },
  {
    slug: "executive",
    label: "Executive",
    name: "Executive operating manual",
    summary: "Quarterly attainment, source attribution, and strategic levers.",
    inside: [
      "Reading the executive dashboard",
      "Source attribution and waterfall",
      "Quota and quarter close review",
      "Reallocation decisions",
    ],
    status: "available",
  },
];

function StatusPill({ status }: { status: "available" | "soon" }) {
  if (status === "available") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          borderRadius: 9999,
          border: "1px solid #E5E5E7",
          color: "#3F3F44",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          background: "#FAFAF7",
        }}
      >
        Available
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 9999,
        border: "1px solid #E5E5E7",
        color: "#6B6B70",
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "#FFFFFF",
      }}
    >
      Coming soon
    </span>
  );
}

function Card({ card }: { card: RoleCard }) {
  const isAvailable = card.status === "available";
  return (
    <article
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        minHeight: 320,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <span
          style={{
            color: RED,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          {card.label}
        </span>
        <StatusPill status={card.status} />
      </div>

      <h3
        style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          color: BLACK,
          fontSize: 20,
          fontWeight: 700,
          margin: "10px 0 6px 0",
          lineHeight: 1.25,
        }}
      >
        {card.name}
      </h3>

      <p
        style={{
          color: "#525258",
          fontSize: 13.5,
          lineHeight: 1.55,
          margin: "0 0 14px 0",
        }}
      >
        {card.summary}
      </p>

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#6B6B70",
            marginBottom: 8,
          }}
        >
          What is inside
        </div>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            color: "#3F3F44",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {card.inside.map((it, i) => (
            <li key={i} style={{ paddingLeft: 14, position: "relative", marginBottom: 4 }}>
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 7,
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: BLACK,
                }}
              />
              {it}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: 18 }}>
        {isAvailable ? (
          <a
            href={`/#/manuals/${card.slug}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 16px",
              borderRadius: 6,
              background: BLACK,
              color: "#FFFFFF",
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Open manual
          </a>
        ) : (
          <a
            href={`/#/manuals/${card.slug}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 16px",
              borderRadius: 6,
              background: "#EFEFEE",
              color: "#6B6B70",
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
              border: "1px solid #E5E5E7",
            }}
          >
            Coming soon
          </a>
        )}
      </div>
    </article>
  );
}

export default function ManualsHome() {
  const [, setLocation] = useLocation();

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Operating Manuals" }]}
    >
      {/* W66.X27 (2026-06-11): iPad/tablet layout fix.
          Removed max-w-7xl from header and main wrappers — the 1280px max-width
          exceeded the available content area inside the 200px-sidebar shell on
          iPad landscape (~1038px content width), causing the page to overflow
          the right edge and clip the header text. Switched to width:100% with
          a sensible internal maxWidth that respects the viewport. */}
      <header style={{ background: "#FFFFFF", borderBottom: "1px solid #E5E5E7" }}>
        <div style={{ width: "100%", maxWidth: 1200, margin: "0 auto", padding: "40px 24px", boxSizing: "border-box" }}>
          <span
            style={{
              color: RED,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            Operating manuals
          </span>
          <h1
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: BLACK,
              fontSize: 36,
              fontWeight: 700,
              lineHeight: 1.1,
              margin: "10px 0 8px 0",
            }}
          >
            Operating Manuals
          </h1>
          <p
            style={{
              color: "#525258",
              fontSize: 16,
              lineHeight: 1.6,
              margin: 0,
              maxWidth: 720,
            }}
          >
            Step-by-step how-to guides for every role inside Attilius.
            Open the manual for your role to see the daily and weekly rhythm, the screens you
            will live in, and the handoffs to and from the rest of the team.
          </p>
        </div>
      </header>

      <main style={{ width: "100%", maxWidth: 1200, margin: "0 auto", padding: "40px 24px", boxSizing: "border-box" }}>
        <div
          style={{
            display: "grid",
            gap: 20,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {CARDS.map((c) => (
            <Card key={c.slug} card={c} />
          ))}
        </div>

        {/* W66.X17: Doctrine banner + Reference Library crosslink */}
        <div
          style={{
            marginTop: 48,
            padding: "24px 28px",
            background: "#0B0B0F",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <div style={{ maxWidth: 560 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: RED,
                marginBottom: 8,
              }}
            >
              Methodology and Doctrine
            </div>
            <p
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 17,
                color: "#FFFFFF",
                lineHeight: 1.5,
                margin: "0 0 6px",
              }}
            >
              The manuals describe how to operate. The Reference Library explains why the
              methodology is built the way it is.
            </p>
            <p style={{ fontSize: 12, color: "#9CA3AF", margin: 0, lineHeight: 1.5 }}>
              M-PACT doctrine, checkpoint models, and the intelligence platform manifesto
              are available in the Reference Library.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setLocation("/library")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "10px 20px",
              borderRadius: 6,
              background: RED,
              color: "#FFFFFF",
              fontSize: 13,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Open Reference Library
          </button>
        </div>

        <p style={{ marginTop: 32, fontSize: 12, color: "#6B6B70", textAlign: "center" }}>
          Mi6 Group · Attilius · v1.0 · W66.X17 · June 2026
        </p>
      </main>
    </OperatorShell>
  );
}
